import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Phone, Mail, Clock } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import BrandLogo from "@/components/BrandLogo";

export default function Footer() {
    const { t } = useLang();
    const { settings } = useSettings();
    const hours = settings.opening_hours || {};
    const dayLabels = t.location?.daysShort || {};
    const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

    return (
        <footer data-testid="site-footer" className="relative bg-brand-charcoal text-white">
            <div className="container-x py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
                <div>
                    <BrandLogo variant="light" />
                    <p className="mt-4 text-sm text-white/70 max-w-xs">
                        {t.footer.tagline} {t.about.body1.split(".")[0]}.
                    </p>
                    <div className="mt-5 flex items-center gap-3">
                        {settings.social_instagram ? (
                            <a href={settings.social_instagram} target="_blank" rel="noreferrer noopener" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 hover:border-brand-yellow transition" aria-label="Instagram">
                                <Instagram className="h-4 w-4" />
                            </a>
                        ) : null}
                        {settings.social_facebook ? (
                            <a href={settings.social_facebook} target="_blank" rel="noreferrer noopener" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 hover:border-brand-yellow transition" aria-label="Facebook">
                                <Facebook className="h-4 w-4" />
                            </a>
                        ) : null}
                    </div>
                </div>

                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-yellow">{t.footer.quick}</div>
                    <ul className="mt-4 space-y-2 text-sm text-white/80">
                        <li><Link className="hover:text-white" to="/angebote">{t.nav.offers}</Link></li>
                        <li><Link className="hover:text-white" to="/kategorien">{t.nav.categories}</Link></li>
                        <li><Link className="hover:text-white" to="/ueber-uns">{t.nav.about}</Link></li>
                        <li><Link className="hover:text-white" to="/karriere">{t.nav.careers}</Link></li>
                        <li><Link className="hover:text-white" to="/faq">{t.nav.faq}</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-yellow">{t.footer.contact}</div>
                    <ul className="mt-4 space-y-2.5 text-sm text-white/80">
                        <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-brand-yellow" /><span>{settings.address || "Sulzbacher Straße 10, 71522 Backnang"}</span></li>
                        {settings.phone && <li className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 text-brand-yellow" /><a className="hover:text-white" href={`tel:${settings.phone.replace(/\s/g, "")}`}>{settings.phone}</a></li>}
                        {settings.email && <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-brand-yellow" /><a className="hover:text-white" href={`mailto:${settings.email}`}>{settings.email}</a></li>}
                    </ul>
                </div>

                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-yellow flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5" /> {t.location.hours}
                    </div>
                    <ul className="mt-4 space-y-1.5 text-sm text-white/80">
                        {dayKeys.map((k) => (
                            <li key={k} className="flex justify-between gap-4">
                                <span className="text-white/60">{dayLabels[k]}</span>
                                <span className="font-medium">{hours[k] || "—"}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="container-x py-5 flex flex-col md:flex-row items-center justify-between gap-3">
                    <p className="text-xs text-white/50">{t.footer.copyright}</p>
                    <div className="flex items-center gap-5 text-xs text-white/60">
                        <Link className="hover:text-white" to="/impressum" data-testid="footer-impressum">{t.footer.impressum}</Link>
                        <Link className="hover:text-white" to="/datenschutz" data-testid="footer-datenschutz">{t.footer.datenschutz}</Link>
                        <Link className="hover:text-white" to="/cookies" data-testid="footer-cookies">{t.footer.cookies}</Link>
                        <Link className="hover:text-white" to="/admin/login" data-testid="footer-admin">Admin</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
